import React, { useState, useEffect } from "react";
import Select from "react-select";
import "./InventoryRegistrationModal.css";
import { createInventory, updateInventory, getInventory } from "../../services/inventoryService";
import { getProducts } from "../../services/productService";
import Popup from "../../components/ui/Popup";

const InventoryRegistrationModal = ({ onClose, onInventoryUpdated,  onInventoryAdded, selectedItem }) => {
    const [products, setProducts] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState("");
    const [reorderLevel, setReorderLevel] = useState("");
    const [popupMessage, setPopupMessage] = useState("");
    const [popupType, setPopupType] = useState("");
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchProducts();
        fetchInventory();
    }, []);
    useEffect(() => {
        if (selectedItem) {
            setSelectedProduct({
                value: selectedItem.product_id,
                label: selectedItem.product_name,
            });
            setQuantity(selectedItem.quantity);
            setReorderLevel(selectedItem.reorder_level);
        }
    }, [selectedItem]);

    // ✅ Fetch Products
    const fetchProducts = async () => {
        try {
            const response = await getProducts();
            if (!response || !Array.isArray(response)) {
                console.error("Invalid product data format", response);
                setProducts([]);
                return;
            }

            const productOptions = response
                .filter(product => product.status !== 0)
                .map(product => ({
                    value: product.id,
                    label: `${product.name} (${product.brand} - ${product.country}) [${product.category_name}]`,
                    price: Number(product.price) || 0
                }));

            setProducts(productOptions);
        } catch (error) {
            console.error("Failed to fetch products:", error);
            setProducts([]);
        }
    };

    // ✅ Fetch Inventory
    const fetchInventory = async () => {
        try {
            const response = await getInventory();
            setInventory(response.data || []);
        } catch (error) {
            console.error("Failed to fetch inventory:", error);
        }
    };

    // ✅ Handle Product Selection & Prefill Data
    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption);

        // Prefill values if product exists in inventory
        const existingItem = inventory.find(item => Number(item.product_id) === Number(selectedOption.value));
        if (existingItem) {
            setQuantity(existingItem.quantity);
            setReorderLevel(existingItem.reorder_level);
        } else {
            setQuantity("");
            setReorderLevel("");
        }
    };

    const handleRegisterOrUpdate = async () => {
        if (!selectedProduct || !quantity || !reorderLevel) {
            setError("All fields are required.");
            return;
        }

        try {
            let requestData;
            let response;

            // ✅ Find if product already exists in inventory
            const existingItem = inventory.find(item => Number(item.product_id) === Number(selectedProduct.value));

            if (selectedItem) {
                // ✅ UPDATE: If editing an existing inventory, replace quantity instead of adding
                requestData = {
                    productId: Number(selectedProduct.value),
                    quantity: Number(quantity), // 🔥 Replace quantity
                };
                response = await updateInventory(requestData);
                setPopupMessage("Inventory updated successfully!");
                onInventoryUpdated();

            } else if (existingItem) {
                // ✅ ADD STOCK: If product exists but "Add Inventory" is clicked, increase quantity
                requestData = {
                    productId: Number(selectedProduct.value),
                    quantity: Number(existingItem.quantity) + Number(quantity), // ✅ Add new stock
                };
                response = await updateInventory(requestData);
                setPopupMessage("Stock quantity increased successfully!");
                onInventoryUpdated();

            } else {
                // ✅ CREATE NEW INVENTORY: If product doesn't exist in stock, add it
                requestData = {
                    productId: Number(selectedProduct.value),
                    quantity: Number(quantity),
                    reorderLevel: Number(reorderLevel),
                };
                response = await createInventory(requestData);
                setPopupMessage("New inventory item added successfully!");
                onInventoryAdded();
            }

            setPopupType("success");
            onClose();
        } catch (error) {
            console.error("Error processing inventory:", error);
            setPopupMessage(error.response?.data?.message || "Failed to process inventory.");
            setPopupType("error");
        }
    };

    return (
        <div className="modal-overlay">
            <Popup message={popupMessage} type={popupType} onClose={() => setPopupMessage("")} />

            <div className="modal-content" style={{ width: "500px", padding: "30px" }}>
                <h2>{inventory.find(item => Number(item.product_id) === Number(selectedProduct?.value)) ? "Update Inventory" : "Register New Inventory"}</h2>
                <hr style={{ marginBottom: "15px", border: "1px solid black" }} />

                <label>Product Name:</label>
                <Select
                    options={products}
                    value={selectedProduct}
                    onChange={handleProductChange}
                    placeholder="Select a Product"
                    isLoading={products.length === 0}
                    noOptionsMessage={() => "No products available"}
                    isDisabled={!!selectedItem} // Disable selection if editing
                />

                <label>Quantity:</label>
                <input
                    type="number"
                    placeholder="Enter Quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                />

                <label>Reorder Level:</label>
                <input
                    type="number"
                    placeholder="Enter Reorder Level"
                    value={reorderLevel}
                    onChange={(e) => setReorderLevel(e.target.value)}
                />

                {error && <p className="error-message">{error}</p>}

                <div className="modal-actions" style={{ marginTop: "20px" }}>
                    <button className="cancel-btn" onClick={onClose}>Cancel</button>
                    <button className="register-btn" onClick={handleRegisterOrUpdate}>
                        {inventory.find(item => Number(item.product_id) === Number(selectedProduct?.value)) ? "Update" : "Register"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InventoryRegistrationModal;
